import { buildHeaders } from 'application/services/buildHeaders';
import { ApiError, ApiRedirect } from 'helpers/error';
import axios, { AxiosRequestConfig } from 'axios';
import { CONTENT_API_URL } from './constants';
import { RequestInterface } from './contentAdapter';
import { AxiosError } from 'helpers/axiosError';

export const postFormToUrl: (url: string, data: any, accessToken: any, locale: string, host: string) => Promise<any> = async (
	url,
	data,
	accessToken,
	locale,
	// host,
) => {
	const method = 'POST';
	const headers = {
		Accept: 'application/json, text/plain, */*',
		'Content-Type': 'application/json',
	};
	headers['Authorization'] = `Bearer ${accessToken}`;
	const contentApiUrl = process.env.NEXT_PUBLIC_CONTENT_API_URL;
	const endpointUrl = `${contentApiUrl}${url}?culture=${locale}`;

	return axios({
		url: endpointUrl,
		method: method,
		maxRedirects: 0,
		headers,
		data,
	})
		.then((res) => {
			const ok = res.status == 200;
			return ok;
		})
		.catch((error) => {
			const message = error?.response?.data?.message ?? error;
			const statusCode = error?.response?.status || 500;

			if (statusCode === 404) {
				throw new ApiError('PageNotFound', 404, 'Page not found');
			}

			if (statusCode === 401 || statusCode === 403) {
				// "401 Unauthorized" really means "unauthenticated"
				// "403 Forbidden" really means "unauthorized"
				return error?.response?.data?.data;
			}

			if (statusCode === 301 || statusCode === 302) {
				throw new ApiRedirect(statusCode, error.response.data.url);
			}

			if (statusCode === 500) {
				throw new ApiError('InternalServerError', 500, message);
			}

			throw new ApiError('Unknown error', statusCode, message);
		});
};

// ContactForm
export const getContactForm = async ({ host, locale, accessToken, method }: RequestInterface): Promise<any> => {
	try {
		if (!CONTENT_API_URL) {
			throw new ApiError('CONTENT_API_URL:NOTDEFINED', 500, 'Content api url is not defined');
		}

		if (host === 'localhost' && process.env.FALLBACK_HOSTNAME) {
			host = process.env.FALLBACK_HOSTNAME;
		}

		// Ensure domain has a protocol - CMS server expects it in Hostname header
		const domain = host as string;

		const query = `{
			pageSearch(documentType:["ContactForm"] limit: 1)
			{
				pages {
					properties
				}
			}
		}`;
		const headers = buildHeaders({
			domain,
			locale,
			accessToken,
		});
		const path = '';
		const url = `${CONTENT_API_URL}${path ? path : ''}`;
		const requestConfig: AxiosRequestConfig = {
			url,
			method: 'POST',
			maxRedirects: 0,
			headers,
			data: {
				query,
			},
		};
		const res = await axios(requestConfig);

		if (method === 'POST') {
			const { data, errors } = res.data;

			if (errors) {
				throw new Error(errors[0].message);
			}

			return {
				content: data.pageSearch.pages[0] || null,
				settings: null,
			};
		}

		if (method === 'GET') {
			const { data } = res;

			return {
				content: data.pageSearch.pages[0] || null,
				settings: null,
			};
		}
	} catch (e) {
		if (axios.isAxiosError(e)) throw new AxiosError(e, { cause: e });
		throw new Error(`getContactForm failed. Cause: ${e.message}`, { cause: e });
	}
};

export const getContactFormSettings = async <TResponse = any>({ host, locale, accessToken, method }: RequestInterface): Promise<TResponse> => {
	try {
		if (!CONTENT_API_URL) {
			throw new ApiError('CONTENT_API_URL:NOTDEFINED', 500, 'Content api url is not defined');
		}

		if (host === 'localhost' && process.env.FALLBACK_HOSTNAME) {
			host = process.env.FALLBACK_HOSTNAME;
		}

		// Ensure domain has a protocol - CMS server expects it in Hostname header
		const domain = host as string;

		const headers = buildHeaders({
			domain,
			locale,
			accessToken,
		});

		const path = `Umbraco/Api/Forms/WebContactOptions?culture=${locale}`;
		const url = `${CONTENT_API_URL}${path ? path : ''}`;

		const res = await axios({
			url,
			method,
			maxRedirects: 0,
			headers,
		});

		if (method === 'GET') {
			const { data } = res;
			return data;
		}
	} catch (error) {
		const message = error?.response?.data?.message ?? error;
		const statusCode = error?.response?.status || 500;

		if (statusCode === 404) {
			throw new ApiError('PageNotFound', 404, 'Page not found');
		}

		if (statusCode === 401 || statusCode === 403) {
			// "401 Unauthorized" really means "unauthenticated"
			// "403 Forbidden" really means "unauthorized"
			return error?.response?.data?.data;
		}

		if (statusCode === 301 || statusCode === 302) {
			throw new ApiRedirect(statusCode, error.response.data.url);
		}

		if (statusCode === 500) {
			throw new ApiError('InternalServerError', 500, message);
		}

		throw new ApiError('Unknown error', statusCode, message);
	}
};

export const postContactFormToUrl: (url: string, data: any, accessToken?: any, locale?: string, host?: string) => Promise<any> = async (
	url,
	data,
) => {
	const method = 'POST';
	const headers = {
		Accept: 'application/json, text/plain, */*',
		'Content-Type': 'application/json',
	};
	const contentApiUrl = process.env.NEXT_PUBLIC_CONTENT_API_URL;
	const endpointUrl = `${contentApiUrl}${url}`;

	return axios({
		url: endpointUrl,
		method: method,
		headers,
		data,
	})
		.then((res) => {
			const ok = res.status == 200;
			return ok;
		})
		.catch((error) => {
			const message = error?.response?.data?.message ?? error;
			const statusCode = error?.response?.status || 500;

			if (statusCode === 404) {
				throw new ApiError('PageNotFound', 404, 'Page not found');
			}

			if (statusCode === 401 || statusCode === 403) {
				// "401 Unauthorized" really means "unauthenticated"
				// "403 Forbidden" really means "unauthorized"
				return error?.response?.data?.data;
			}

			if (statusCode === 301 || statusCode === 302) {
				throw new ApiRedirect(statusCode, error.response.data.url);
			}

			if (statusCode === 500) {
				throw new ApiError('InternalServerError', 500, message);
			}

			throw new ApiError('Unknown error', statusCode, message);
		});
};

// SignupForm
export const getSignupForm = <TResponse = any>({ host, locale, accessToken }: RequestInterface): Promise<TResponse> => {
	if (!CONTENT_API_URL) {
		throw new ApiError('CONTENT_API_URL:NOTDEFINED', 500, 'Content api url is not defined');
	}

	if (host === 'localhost' && process.env.FALLBACK_HOSTNAME) {
		host = process.env.FALLBACK_HOSTNAME;
	}

	const domain = host as string;
	const query = `{
		pageSearch(documentType:["modalSignupForm"] limit: 1)
		{
			pages {
				properties
			}
		}
	}`;
	const headers = buildHeaders({
		domain,
		locale,
		accessToken,
	});
	const path = '';
	const method = 'POST';
	const url = `${CONTENT_API_URL}${path ? path : ''}`;

	return axios({
		url,
		method,
		maxRedirects: 0,
		headers,
		data: {
			query,
		},
	})
		.then((res) => {
			if (method === 'POST') {
				const { data, errors } = res.data;

				if (errors) {
					throw new Error(errors[0].message);
				}

				return {
					content: data.pageSearch.pages[0] || null,
					settings: null,
				};
			}

			if (method === 'GET') {
				const { data } = res;

				return {
					content: data.pageSearch.pages[0] || null,
					settings: null,
				};
			}
		})
		.catch((error) => {
			const message = error?.response?.data?.message ?? error;
			const statusCode = error?.response?.status || 500;

			if (statusCode === 404) {
				throw new ApiError('PageNotFound', 404, 'Page not found');
			}

			if (statusCode === 401 || statusCode === 403) {
				// "401 Unauthorized" really means "unauthenticated"
				// "403 Forbidden" really means "unauthorized"
				return error?.response?.data?.data;
			}

			if (statusCode === 301 || statusCode === 302) {
				throw new ApiRedirect(statusCode, error.response.data.url);
			}

			if (statusCode === 500) {
				throw new ApiError('InternalServerError', 500, message);
			}

			throw new ApiError('Unknown error', statusCode, message);
		});
};
